import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import { common, createLowlight } from 'lowlight';

import { AdmonitionNode } from './extensions/Admonition';
import { MermaidBlockNode } from './extensions/MermaidBlock';
import { ImageCaptionNode } from './extensions/ImageCaption';
import { Toolbar } from './Toolbar';
import { ImageUploadDialog } from './ImageUploadDialog';
import { LinkDialog } from './LinkDialog';
import { fromMarkdown } from './markdown/fromMarkdown';
import { toMarkdown } from './markdown/toMarkdown';
import type { JSONContent } from '@tiptap/core';

const lowlight = createLowlight(common);

interface TipTapEditorProps {
  initialMarkdown: string;
  onChange: (markdown: string) => void;
  placeholder?: string;
}

export const TipTapEditor: React.FC<TipTapEditorProps> = ({
  initialMarkdown,
  onChange,
  placeholder = 'Start writing...',
}) => {
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [currentLinkUrl, setCurrentLinkUrl] = useState('');
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        codeBlock: false, // Replaced by CodeBlockLowlight
      }),
      Image.configure({
        inline: true,
        allowBase64: true,
        HTMLAttributes: {
          class: 'editor-image',
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 underline hover:text-blue-800',
        },
      }),
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Placeholder.configure({
        placeholder,
      }),
      CodeBlockLowlight.configure({
        lowlight,
        HTMLAttributes: {
          class: 'code-block',
        },
      }),
      AdmonitionNode,
      MermaidBlockNode,
      ImageCaptionNode,
    ],
    content: fromMarkdown(initialMarkdown),
    onUpdate: ({ editor }) => {
      const json = editor.getJSON();
      const markdown = toMarkdown(json);
      onChangeRef.current(markdown);
    },
  });

  // Load new content when initialMarkdown changes externally
  const prevMarkdownRef = useRef(initialMarkdown);
  useEffect(() => {
    if (editor && initialMarkdown !== prevMarkdownRef.current) {
      prevMarkdownRef.current = initialMarkdown;
      const newContent = fromMarkdown(initialMarkdown);
      editor.commands.setContent(newContent, false);
    }
  }, [editor, initialMarkdown]);

  const handleImageInsert = useCallback(
    (url: string, alt: string, width?: number) => {
      if (!editor) return;
      editor
        .chain()
        .focus()
        .insertContent({
          type: 'image',
          attrs: { src: url, alt, width: width ?? null },
        })
        .run();
      setShowImageDialog(false);
    },
    [editor]
  );

  const handleLinkInsert = useCallback(
    (url: string, text?: string) => {
      if (!editor) return;
      if (text) {
        editor
          .chain()
          .focus()
          .insertContent({
            type: 'text',
            text,
            marks: [{ type: 'link', attrs: { href: url } }],
          })
          .run();
      } else {
        editor.chain().focus().setLink({ href: url }).run();
      }
      setShowLinkDialog(false);
    },
    [editor]
  );

  const handleLinkRemove = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().unsetLink().run();
    setShowLinkDialog(false);
  }, [editor]);

  const handleLinkOpen = useCallback(() => {
    if (!editor) return;
    const attrs = editor.getAttributes('link');
    setCurrentLinkUrl(attrs['href'] || '');
    setShowLinkDialog(true);
  }, [editor]);

  return (
    <div className="flex flex-col h-full">
      <Toolbar
        editor={editor}
        onImageUpload={() => setShowImageDialog(true)}
        onLinkAdd={handleLinkOpen}
      />

      <div className="flex-1 overflow-auto">
        <EditorContent
          editor={editor}
          className="h-full prose prose-sm max-w-none"
        />
      </div>

      {showImageDialog && (
        <ImageUploadDialog
          onInsert={handleImageInsert}
          onClose={() => setShowImageDialog(false)}
        />
      )}

      {showLinkDialog && (
        <LinkDialog
          initialUrl={currentLinkUrl}
          onInsert={handleLinkInsert}
          onRemove={currentLinkUrl ? handleLinkRemove : undefined}
          onClose={() => setShowLinkDialog(false)}
        />
      )}
    </div>
  );
};

// Export for getting markdown from the editor externally
export function editorToMarkdown(content: JSONContent): string {
  return toMarkdown(content);
}
